import type { BudgetBand, CurrentState, EstimateInput, EstimateResult, ProjectType, Quality, Region } from './model';

type Range = [number, number];

const region: Record<Region, number> = { madrid: 1.12, barcelona: 1.14, valencia: 1, andalucia: 0.94, rest: 1 };
const state: Record<CurrentState, number> = { good: 1, lived: 1.06, old: 1.14, structural: 1.25 };
const integral: Record<Quality, Range> = { basic: [500, 750], standard: [750, 1100], premium: [1100, 1550] };
const kitchenSqm: Record<Quality, Range> = { basic: [600, 950], standard: [900, 1350], premium: [1250, 1850] };
const kitchenMin: Record<Quality, Range> = { basic: [4000, 8500], standard: [6000, 14500], premium: [12000, 28000] };
const bath: Record<Quality, Range> = { basic: [2200, 4500], standard: [4200, 8000], premium: [7500, 13000] };
const floor: Record<Quality, Range> = { basic: [28, 48], standard: [45, 85], premium: [80, 145] };
const paint: Record<Quality, Range> = { basic: [8, 14], standard: [12, 22], premium: [20, 38] };
const labels: Record<ProjectType, string> = { integral: 'reforma integral', kitchen: 'reforma de cocina', bathroom: 'reforma de baño', flooring: 'cambio de suelos', painting: 'pintura' };

export function validateEstimateInput(input: EstimateInput): string[] {
  const errors: string[] = [];
  if (!Number.isFinite(input.areaSqm) || input.areaSqm < 2 || input.areaSqm > 350) errors.push('Indica una superficie entre 2 y 350 m².');
  if (!/^\d{5}$/.test(input.postalCode.trim())) errors.push('Indica un código postal español de 5 dígitos.');
  if (!Number.isInteger(input.bathrooms) || input.bathrooms < 0 || input.bathrooms > 6) errors.push('Indica entre 0 y 6 baños.');
  if (input.projectType === 'bathroom' && input.bathrooms < 1) errors.push('Para una reforma de baño debe haber al menos 1 baño.');
  if (input.projectType === 'integral' && input.areaSqm < 25) errors.push('Para reforma integral, usa al menos 25 m².');
  return errors;
}

export function calculateEstimate(input: EstimateInput): EstimateResult {
  const errors = validateEstimateInput(input);
  if (errors.length) throw new Error(errors.join(' '));

  let range: Range;
  const assumptions = [`Zona ${input.region} x${region[input.region].toFixed(2)}.`, `Calidad ${input.quality}.`, `Estado ${input.currentState}.`];
  const risks: string[] = [];

  if (input.projectType === 'integral') {
    range = mul(integral[input.quality], input.areaSqm);
    assumptions.push(`Base integral por m² sobre ${input.areaSqm} m².`);
    if (input.bathrooms > 1) range = add(range, mul([2000, 6000], input.bathrooms - 1));
    if (!input.kitchenIncluded) range = mul(range, 0.88);
  } else if (input.projectType === 'kitchen') {
    const sqm = mul(kitchenSqm[input.quality], input.areaSqm);
    const min = kitchenMin[input.quality];
    range = [Math.max(sqm[0], min[0]), Math.max(sqm[1], min[1])];
    assumptions.push('Cocina con mínimo de proyecto por mobiliario, encimera e instalaciones.');
  } else if (input.projectType === 'bathroom') {
    range = mul(bath[input.quality], Math.max(1, input.bathrooms));
    assumptions.push('Baño completo por unidad.');
  } else if (input.projectType === 'flooring') {
    range = mul(floor[input.quality], input.areaSqm);
    assumptions.push('Suelos por m² sin nivelación estructural severa.');
  } else {
    range = mul(paint[input.quality], input.areaSqm * 2.6);
    assumptions.push('Pintura por superficie equivalente de paredes y techos.');
  }

  range = mul(range, region[input.region] * state[input.currentState]);
  if (input.changesDistribution) { range = add(range, mul(range, 0.09)); assumptions.push('Incluye impacto por distribución.'); risks.push('Revisar muros de carga y comunidad.'); }
  if (input.changesInstallations) { range = add(range, mul(range, 0.12)); assumptions.push('Incluye impacto por instalaciones.'); risks.push('Revisar electricidad, fontanería y climatización.'); }
  if (input.needsPermit) { range = add(range, [350, Math.max(900, range[1] * 0.05)]); assumptions.push('Incluye colchón de licencia/comunicación.'); risks.push('Confirmar trámite municipal.'); }
  if (input.currentState === 'structural') risks.push('Baja confianza hasta visita técnica por patologías.');
  if (input.currentState === 'old') risks.push('Posibles desvíos por instalaciones ocultas antiguas.');

  const rounded = round(range);
  return {
    min: rounded[0],
    max: rounded[1],
    midpoint: Math.round((rounded[0] + rounded[1]) / 2),
    label: labels[input.projectType],
    vatNote: 'IVA no incluido; el tipo aplicable depende del caso.',
    assumptions,
    risks: risks.length ? risks : ['Sin riesgos críticos declarados; la visita sigue siendo necesaria.'],
    confidence: input.currentState === 'structural' || input.changesDistribution || input.changesInstallations ? 'baja' : 'media'
  };
}

export { getBudgetFit } from './qualification';
export function formatCurrency(n: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}
export function formatRange(min: number, max: number): string { return `${formatCurrency(min)} – ${formatCurrency(max)}`; }

const mul = (r: Range, m: number): Range => [r[0] * m, r[1] * m];
const add = (a: Range, b: Range): Range => [a[0] + b[0], a[1] + b[1]];
const round = (r: Range): Range => roundTo(r, r[1] > 20000 ? 500 : 100);
const roundTo = (r: Range, n: number): Range => [Math.max(n, Math.round(r[0] / n) * n), Math.max(n, Math.round(r[1] / n) * n)];
